<template>
  <v-card class="mb-4">
    <v-card-title>
      <div class="d-flex align-center">
        {{ selectedItem }}
        <v-tooltip :text="tooltip" location="top" v-if="tooltip && tooltip.length > 0">
          <template v-slot:activator="{ props }">
            <v-icon
              v-bind="props"
              icon="mdi-information"
              size="small"
              class="ml-2"
            ></v-icon>
          </template>
        </v-tooltip>
        <v-select
          v-model="selected"
          :items="items"
          variant="plain"
          hide-details
          class="select-hidden"
          @update:modelValue="handleSelectionChange"
        ></v-select>
      </div>
    </v-card-title>
    <v-card-text class="text-h3">
      {{ displayCount }}
    </v-card-text>
  </v-card>
</template>

<script setup>
import { ref, defineProps, computed, watch } from 'vue';
import { useStore } from 'vuex';

const props = defineProps({
  count: {
    type: Number,
    required: true
  },
  items: {
    type: Array,
    required: true
  },
  tooltip: {
    type: String,
    required: true
  }
});

const store = useStore();
const showOpenItemsOnly = computed(() => store.getters.getShowOpenItemsOnly);
const selectedItem = ref(showOpenItemsOnly.value ? props.items[1] : props.items[0]);
const selected = ref(showOpenItemsOnly.value ? props.items[1] : props.items[0]);

// Add animation-related variables
const displayCount = ref(props.count);
const intervalId = ref(null);
const firstChangeCompleted = ref(false);

// Function to animate the count
const animateCount = (start, end) => {
  // Clear any existing animation
  if (intervalId.value) {
    clearInterval(intervalId.value);
  }
  
  // Calculate step size and animation parameters
  const difference = end - start;
  const duration = 1000; // animation duration in ms
  const framesPerSecond = 30;
  const totalFrames = duration / (1000 / framesPerSecond);
  
  // Determine appropriate step size based on the difference
  let step;
  if (Math.abs(difference) < 20) {
    step = Math.max(1, Math.abs(Math.ceil(difference / totalFrames)));
  } else {
    step = Math.max(1, Math.abs(Math.ceil(difference / 20)));
  }
  
  // Set direction based on whether we're counting up or down
  if (difference < 0) {
    step = -step;
  }
  
  let current = start;
  displayCount.value = current;
  
  intervalId.value = setInterval(() => {
    // Add step to current value
    current += step;
    
    // Check if we've reached or passed the target
    if ((step > 0 && current >= end) || (step < 0 && current <= end)) {
      displayCount.value = end;
      clearInterval(intervalId.value);
    } else {
      displayCount.value = current;
    }
  }, 1000 / framesPerSecond);
};

// Watch for changes in the count prop
watch(() => props.count, (newCount, oldCount) => {
  // First time the watcher runs (immediate execution)
  if (oldCount === undefined) {
    displayCount.value = newCount;
    return;
  }
  
  // No change in value
  if (newCount === oldCount) {
    return;
  }
  
  // First actual change in value
  if (!firstChangeCompleted.value) {
    displayCount.value = newCount;
    firstChangeCompleted.value = true;
    return;
  }
  
  // All subsequent changes - animate
  animateCount(oldCount, newCount);
}, { immediate: true });

const handleSelectionChange = async (value) => {
  if (value === props.items[1]) {
    store.commit('setShowOpenItemsOnly', true);
  } else {
    store.commit('setShowOpenItemsOnly', false);
  }

  store.commit('setShowWelcomeDialog', true);
  store.commit('setLoadDashboard', true);
};
</script>

<style scoped>
.select-hidden {
  width: 24px;
}
.select-hidden :deep(.v-field__input) {
  display: none;
}
.select-hidden :deep(.v-field) {
  padding: 0;
}
.select-hidden :deep(.v-field__append-inner) {
  padding-top: 0!important;
}
</style>